import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Spin } from 'antd';
import AppLayout from '../components/layouts/AppLayout';
import { Login } from '../screens/login/Login';
import PrivateRoute from "./PrivateRoute";
import { Breadcrumb } from '../components/breadcrumb/Breadcrumb';
import { displayMessage } from '../helpers/messages';

// Screens - Drums (usadas pelo PWA do operador — eager, leves)
import DrumEntry from '../screens/drum/DrumEntry';
import ProcessDrum from '../screens/drum/ProcessDrum';
import ConferenceDrum from '../screens/drum/ConferenceDrum';

// Screens - Operations (compartilhadas admin/operador, com modo mobile)
import RcPanel from '../screens/operations/RcPanel';
import RcConsumptionPanel from '../screens/operations/RcConsumptionPanel';
import DrenagemPanel from '../screens/operations/DrenagemPanel';
import AmarelamentoPanel from '../screens/operations/AmarelamentoPanel';
import TanquePanel from '../screens/operations/TanquePanel';

// Operator Home (PWA)
import OperatorHome from '../screens/operator/OperatorHome';

// Login
import { LoginForm } from '../screens/login/LoginForm';
import { ResetPasswordForm } from '../screens/login/ResetPasswordForm';
import ChangePassword from '../screens/login/ChangePassword';

// Screens - Profile
import DetailProfile from '../screens/user/DetailProfile';

// Guards por papel
import RoleRoute from './RoleRoute';
import HomeRedirect from './HomeRedirect';
import EUserTypes from '../enums/EUserTypes';

// Screens admin-only carregadas sob demanda (lazy) para manter o bundle do
// operador leve no PWA. Declarações após todos os imports (regra import/first).
const ListUsers = React.lazy(() => import('../screens/user/ListUsers'));
const DetailUser = React.lazy(() => import('../screens/user/DetailUser'));
const ListClients = React.lazy(() => import('../screens/client/ListClients'));
const DetailClient = React.lazy(() => import('../screens/client/DetailClient'));
const ListBatches = React.lazy(() => import('../screens/batch/ListBatches'));
const DetailBatch = React.lazy(() => import('../screens/batch/DetailBatch'));
const Dashboard = React.lazy(() => import('../screens/dashboard/Dashboard'));
const StockDashboard = React.lazy(() => import('../screens/stock/StockDashboard'));
const ListDrums = React.lazy(() => import('../screens/drum/ListDrums'));
const DetailDrum = React.lazy(() => import('../screens/drum/DetailDrum'));
const WithdrawalPanel = React.lazy(() => import('../screens/operations/WithdrawalPanel'));
const ListAuditLogs = React.lazy(() => import('../screens/auditLog/ListAuditLogs'));
const ListMachines = React.lazy(() => import('../screens/machine/ListMachines'));
const ListTemperatureReadings = React.lazy(() => import('../screens/temperature/ListTemperatureReadings'));
const DetailMachine = React.lazy(() => import('../screens/machine/DetailMachine'));
const DrumView = React.lazy(() => import('../screens/drum/DrumView'));
const LiderTotals = React.lazy(() => import('../screens/lider/LiderTotals'));
const EditDrums = React.lazy(() => import('../screens/drum/EditDrums'));
const ProcessedByMachine = React.lazy(() => import('../screens/drum/ProcessedByMachine'));
const LossesPanel = React.lazy(() => import('../screens/losses/LossesPanel'));

const ADMIN_ONLY = [EUserTypes.PANEL_ADMIN.code];
const OPERATORS_AND_ADMIN = [EUserTypes.PANEL_ADMIN.code, EUserTypes.OPERADOR.code, EUserTypes.OPERADOR_COMPRADOR.code, EUserTypes.LIDER.code];
const LIDER_AND_ADMIN = [EUserTypes.PANEL_ADMIN.code, EUserTypes.LIDER.code];
const CONFERENCE_ROLES = [EUserTypes.PANEL_ADMIN.code, EUserTypes.OPERADOR_COMPRADOR.code, EUserTypes.LIDER.code];

const LazyFallback = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <Spin size="large" />
  </div>
);

const lazy = (element) => <Suspense fallback={LazyFallback}>{element}</Suspense>;

const router = (
  <>
    <Route path='/' element={<PrivateRoute />}>
      <Route element={<AppLayout />}>
        {/* Home do PWA (operadores) */}
        <Route
          path="home"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              <OperatorHome />
            </RoleRoute>
          }
        />

        {/* Usuários */}
        <Route path='users'>
          <Route
            index
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb
                  listCrumbs={[{ label: displayMessage('USERS'), link: '/users' }]}>
                  {lazy(<ListUsers />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />

          <Route
            path=':idUser'
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb
                  listCrumbs={[
                    { label: displayMessage('USERS'), link: '/users' },
                    { label: displayMessage('USER') + ': {idUser}', params: ['idUser'] }
                  ]}>
                  {lazy(<DetailUser />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />
        </Route>

        {/* Clientes */}
        <Route path='clients'>
          <Route
            index
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb
                  listCrumbs={[{ label: displayMessage('CLIENTS'), link: '/clients' }]}>
                  {lazy(<ListClients />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />

          <Route
            path=':idClient'
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb
                  listCrumbs={[
                    { label: displayMessage('CLIENTS'), link: '/clients' },
                    { label: displayMessage('CLIENT') + ': {idClient}', params: ['idClient'] }
                  ]}>
                  {lazy(<DetailClient />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />
        </Route>

        {/* Dashboard */}
        <Route
          path="dashboard"
          element={
            <RoleRoute allowedRoles={ADMIN_ONLY}>
              <Breadcrumb listCrumbs={[{ label: 'Dashboard' }]}>
                {lazy(<Dashboard />)}
              </Breadcrumb>
            </RoleRoute>
          }
        />

        {/* Estoque */}
        <Route
          path="stock"
          element={
            <RoleRoute allowedRoles={ADMIN_ONLY}>
              <Breadcrumb listCrumbs={[{ label: 'Estoque' }]}>
                {lazy(<StockDashboard />)}
              </Breadcrumb>
            </RoleRoute>
          }
        />

        {/* Tambores (Admin) */}
        <Route path='drums'>
          <Route
            index
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb listCrumbs={[{ label: 'Tambores', link: '/drums' }]}>
                  {lazy(<ListDrums />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />
          <Route
            path=':idDrum'
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb
                  listCrumbs={[
                    { label: 'Tambores', link: '/drums' },
                    { label: 'Tambor: {idDrum}', params: ['idDrum'] }
                  ]}>
                  {lazy(<DetailDrum />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />
        </Route>

        {/* Entrada por QR (fluxo operacional - Operadores + Admin) */}
        <Route
          path="entry"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              <DrumEntry />
            </RoleRoute>
          }
        />

        {/* Processamento e Conferência (fluxos por tambor) */}
        <Route
          path="drums/:idDrum/process"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              <ProcessDrum />
            </RoleRoute>
          }
        />
        <Route
          path="drums/:idDrum/conference"
          element={
            <RoleRoute allowedRoles={CONFERENCE_ROLES}>
              <ConferenceDrum />
            </RoleRoute>
          }
        />
        {/* View só-leitura do tambor (operador + admin) — tambor já processado/conferido */}
        <Route
          path="drums/:idDrum/view"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              {lazy(<DrumView />)}
            </RoleRoute>
          }
        />
        {/* Correção de tambores (compra/processamento, qualquer data, com
            justificativa) — Operador-comprador, Líder e Admin */}
        <Route
          path="drums/edit"
          element={
            <RoleRoute allowedRoles={CONFERENCE_ROLES}>
              {lazy(<EditDrums />)}
            </RoleRoute>
          }
        />
        {/* Tambores processados por dia/máquina — PWA de todos os operadores */}
        <Route
          path="drums/processed-by-machine"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              {lazy(<ProcessedByMachine />)}
            </RoleRoute>
          }
        />

        {/* Operações (RC, Descarga, Retirada) - rotas separadas */}
        <Route
          path="operations/rc"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              <RcPanel />
            </RoleRoute>
          }
        />
        <Route
          path="operations/rc-consumption"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              <RcConsumptionPanel />
            </RoleRoute>
          }
        />
        <Route
          path="operations/drenagem"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              <DrenagemPanel />
            </RoleRoute>
          }
        />
        <Route
          path="operations/amarelamento"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              <AmarelamentoPanel />
            </RoleRoute>
          }
        />
        <Route
          path="operations/tanque"
          element={
            <RoleRoute allowedRoles={OPERATORS_AND_ADMIN}>
              <TanquePanel />
            </RoleRoute>
          }
        />

        {/* Totais do dia (Líder + Admin) - tela mobile do Líder */}
        <Route
          path="lider-totals"
          element={
            <RoleRoute allowedRoles={LIDER_AND_ADMIN}>
              {lazy(<LiderTotals />)}
            </RoleRoute>
          }
        />
        <Route
          path="operations/retirada"
          element={
            <RoleRoute allowedRoles={ADMIN_ONLY}>
              <Breadcrumb listCrumbs={[{ label: 'Vendas' }]}>
                {lazy(<WithdrawalPanel />)}
              </Breadcrumb>
            </RoleRoute>
          }
        />

        {/* Lotes (Batches) - Admin */}
        <Route path='batches'>
          <Route
            index
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb
                  listCrumbs={[{ label: displayMessage('BATCHES'), link: '/batches' }]}>
                  {lazy(<ListBatches />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />

          <Route
            path=':idBatch'
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb
                  listCrumbs={[
                    { label: displayMessage('BATCHES'), link: '/batches' },
                    { label: displayMessage('BATCH') + ': {idBatch}', params: ['idBatch'] }
                  ]}>
                  {lazy(<DetailBatch />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />
        </Route>

        {/* Temperatura — histórico de leituras (Admin) */}
        <Route
          path="temperature"
          element={
            <RoleRoute allowedRoles={ADMIN_ONLY}>
              <Breadcrumb listCrumbs={[{ label: 'Temperatura' }]}>
                {lazy(<ListTemperatureReadings />)}
              </Breadcrumb>
            </RoleRoute>
          }
        />

        {/* Perdas e zeramentos (Admin) */}
        <Route
          path="losses"
          element={
            <RoleRoute allowedRoles={ADMIN_ONLY}>
              <Breadcrumb listCrumbs={[{ label: 'Perdas' }]}>
                {lazy(<LossesPanel />)}
              </Breadcrumb>
            </RoleRoute>
          }
        />

        {/* Logs de Auditoria (Admin) */}
        <Route
          path="audit-logs"
          element={
            <RoleRoute allowedRoles={ADMIN_ONLY}>
              <Breadcrumb listCrumbs={[{ label: 'Logs de Auditoria' }]}>
                {lazy(<ListAuditLogs />)}
              </Breadcrumb>
            </RoleRoute>
          }
        />

        {/* Máquinas (Admin) */}
        <Route path="machines">
          <Route
            index
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb listCrumbs={[{ label: 'Máquinas' }]}>
                  {lazy(<ListMachines />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />
          <Route
            path=":idMachine"
            element={
              <RoleRoute allowedRoles={ADMIN_ONLY}>
                <Breadcrumb listCrumbs={[{ label: 'Máquinas', link: '/machines' }, { label: 'Máquina: {idMachine}', params: ['idMachine'] }]}>
                  {lazy(<DetailMachine />)}
                </Breadcrumb>
              </RoleRoute>
            }
          />
        </Route>

        {/* Perfil */}        <Route
          path="profile"
          element={
            <Breadcrumb
              listCrumbs={[{ label: displayMessage('MY_PROFILE') }]}
            >
              <DetailProfile />
            </Breadcrumb>
          }
        />

        {/* Redirect raiz baseado no papel */}
        <Route
          index
          element={<HomeRedirect />}
        />
      </Route>

      {/* Alterar senha */}
      <Route path="change-password" element={<ChangePassword />} />
    </Route>

    {/* Login */}
    <Route path="login" element={<Login />}>
      <Route index element={<LoginForm />} />
      <Route path="reset-password/:token" element={<ResetPasswordForm />} />
    </Route>
  </>
);

function Router() {
  return (
    <BrowserRouter>
      <Routes>
        {router}
      </Routes>
    </BrowserRouter>
  );
}

export default Router;



